import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  List,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import {
  School as SchoolIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpOutlineIcon,
  Dashboard,
  Class,
  Subject,
  Assignment,
  Schedule,
  Person,
  EventNote,
  CalendarToday,
  CheckCircle,
  Receipt,
  TrendingUp,
  Event,
  Photo,
  Hotel,
  AdminPanelSettings,
  AccountBalanceWallet,
  ViewList,
  VerifiedUser,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import EmptyState from '../common/EmptyState';
import { useAdminNavigation } from '../navigation/AdminNavigationContext';

const DRAWER_WIDTH = 270;

const iconMapping = {
  overview: <Dashboard />,
  classes: <Class />,
  sections: <ViewList />,
  subjects: <Subject />,
  class_subject_assignment: <Assignment />,
  timetables: <Schedule />,
  assign_teacher: <Person />,
  attendance: <EventNote />,
  leave_balance: <CalendarToday />,
  leave_requests: <CheckCircle />,
  regularization: <EventNote />,
  manage_exams: <Assignment />,
  results: <CheckCircle />,
  fee_collections: <Receipt />,
  expenses: <TrendingUp />,
  event_management: <Event />,
  holidays: <CalendarToday />,
  hostel_management: <Hotel />,
  reports: <TrendingUp />,
  'photo-gallery': <Photo />,
  school_settings: <SettingsIcon />,
  admin_settings: <AdminPanelSettings />,
  subscription_management: <AccountBalanceWallet />,
  app_subscription_management: <AccountBalanceWallet />,
  message_balance: <AccountBalanceWallet />,
  manage_schools: <SchoolIcon />,
  students: <SchoolIcon />,
  employees: <GroupIcon />,
  account: <Person />,
  teacher_dashboard: <Dashboard />,
  student_dashboard: <Dashboard />,
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    height: '100vh',
    margin: 0,
    position: 'sticky',
    top: 0,
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
    borderRight: `1px solid ${alpha('#334155', 0.8)}`,
    boxShadow: 'none',
    overflowX: 'hidden',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  color: '#F8FAFC',
  padding: theme.spacing(2, 1.8, 1.5, 1.8),
  minHeight: 76,
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  borderBottom: `1px solid ${alpha('#334155', 0.6)}`,
  background: 'rgba(255,255,255,0.02)',
}));

const SchoolNameText = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '0.94rem',
  color: '#FFFFFF',
  letterSpacing: '-0.01em',
  lineHeight: 1.25,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const SchoolMetaText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.62rem',
  color: '#10B981',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  lineHeight: 1.2,
  marginBottom: theme.spacing(0.3),
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  position: 'relative',
  minHeight: 38,
  padding: theme.spacing(0.6, 1.2),
  borderRadius: 6,
  margin: theme.spacing(0.15, 0.8),
  background: selected ? alpha('#059669', 0.22) : 'transparent',
  border: selected ? `1px solid ${alpha('#10B981', 0.3)}` : '1px solid transparent',
  transition: 'all 0.15s ease',
  '&:hover': {
    background: selected ? alpha('#059669', 0.3) : alpha('#FFFFFF', 0.05),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 3,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: 2,
    background: selected ? '#10B981' : 'transparent',
  },
}));

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  minWidth: 32,
  color: selected ? '#10B981' : alpha('#E2E8F0', 0.72),
  '& .MuiSvgIcon-root': {
    fontSize: '1.1rem',
  },
}));

const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  '& .MuiTypography-root': {
    fontSize: '0.84rem',
    color: selected ? '#FFFFFF' : alpha('#E2E8F0', 0.82),
    fontWeight: selected ? 700 : 500,
    letterSpacing: '0.005em',
  },
}));

const Sidebar = ({ mobileOpen, handleDrawerToggle, schoolName = 'School Dashboard' }) => {
  const { modules, menuLoading, menuError } = useAdminNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleItemClick = useCallback(
    (path) => {
      if (path) {
        navigate(path);
        if (isMobile && mobileOpen) {
          handleDrawerToggle();
        }
      }
    },
    [navigate, isMobile, mobileOpen, handleDrawerToggle]
  );

  const renderSingleItem = useCallback(
    (item) => {
      const itemPath = item?.path || '';
      const [itemPathname, itemSearch = ''] = itemPath.split('?');
      const currentUrl = `${location.pathname}${location.search || ''}`;
      const itemUrl = itemSearch ? `${itemPathname}?${itemSearch}` : itemPathname;
      const isActive = Boolean(
        itemPath &&
        (
          currentUrl === itemUrl ||
          (!itemSearch && (location.pathname === itemPathname || location.pathname.startsWith(`${itemPathname}/`)))
        )
      );
      const itemIcon = iconMapping[item.icon] || <HelpOutlineIcon fontSize="small" />;

      return (
        <StyledListItemButton
          key={item.path || item.text || item.title}
          onClick={() => handleItemClick(item.path)}
          selected={isActive}
          disabled={!item.path}
        >
          <StyledListItemIcon selected={isActive}>{itemIcon}</StyledListItemIcon>
          <StyledListItemText primary={item.text || item.title} selected={isActive} />
        </StyledListItemButton>
      );
    },
    [handleItemClick, location.pathname, location.search]
  );

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <HeaderContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              mr: 1.25,
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
            }}
          >
            <SchoolIcon sx={{ fontSize: 20, color: '#FFFFFF' }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <SchoolMetaText>Enterprise Portal</SchoolMetaText>
            <SchoolNameText variant="h6">{schoolName === 'N/A' ? 'SKHOOL' : schoolName}</SchoolNameText>
          </Box>
        </Box>
      </HeaderContainer>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
          py: 1,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha('#10B981', 0.2),
            borderRadius: 2,
          },
        }}
      >
        {menuLoading ? (
          <Box sx={{ px: 1.8, py: 1.6 }}>
            <Typography variant="caption" sx={{ color: alpha('#94A3B8', 0.95), fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Loading navigation...
            </Typography>
          </Box>
        ) : menuError ? (
          <EmptyState
            title="Navigation unavailable"
            description={typeof menuError === 'string' ? menuError : 'Failed to load menu.'}
            icon={<SettingsIcon fontSize="small" />}
            sx={{ py: 2.4 }}
          />
        ) : !modules?.length ? (
          <EmptyState title="No accessible sections" description="No items found." sx={{ py: 2.8 }} />
        ) : (
          modules.map((module) => {
            const items = module.items || [];
            if (!items.length) return null;
            return (
              <Box key={module.key || module.label} sx={{ mb: 1.5 }}>
                <Box sx={{ px: 1.6, pt: 0.5, pb: 0.4 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha('#10B981', 0.85),
                      fontWeight: 800,
                      fontSize: '0.64rem',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {module.label}
                  </Typography>
                </Box>
                <List disablePadding>
                  {items.map((item) => renderSingleItem(item))}
                </List>
              </Box>
            );
          })
        )}
      </Box>

      {/* Footer Status Card */}
      <Box
        sx={{
          p: 1,
          m: 0.8,
          borderRadius: 1.5,
          bgcolor: alpha('#10B981', 0.08),
          border: `1px solid ${alpha('#10B981', 0.16)}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <VerifiedUser sx={{ fontSize: 16, color: '#10B981' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 700, display: 'block', fontSize: '0.72rem' }}>
            System Verified
          </Typography>
          <Typography variant="caption" sx={{ color: alpha('#E2E8F0', 0.7), fontSize: '0.64rem' }}>
            All Services Operational
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
      <StyledDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: `min(${DRAWER_WIDTH}px, 100%)`,
            margin: 0,
            height: '100%',
            borderRadius: 0,
            position: 'fixed',
          },
        }}
      >
        {drawerContent}
      </StyledDrawer>
      <StyledDrawer variant="permanent" open sx={{ display: { xs: 'none', md: 'block' } }}>
        {drawerContent}
      </StyledDrawer>
    </Box>
  );
};

Sidebar.propTypes = {
  mobileOpen: PropTypes.bool.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
  schoolName: PropTypes.string,
};

export default Sidebar;
